import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, MenuProps, theme as antTheme } from 'antd';

import LeftNavLogoLarge from '@totem/components/logos/LeftNavLogoLarge';
import LeftNavLogoSmall from '@totem/components/logos/LeftNavLogoSmall';
import MainMenuContext from '@totem/components/ui_components/menus/menuContext';
import { IBMenuItem } from '@totem/components/ui_components/menus/types';
import { createMenuLabel } from '@totem/components/ui_components/menus/utilities';
import TokenExpirationMonitor from '@totem/components/userContext/TokenExpirationMonitor';
import { staticBranding } from '@totem/selectors/preferencesSelectors';
import { BrandingPreference } from '@totem/types/organization';
import { isNotNull } from '@totem/utilities/common';

import '../../base.css';

const { Sider } = Layout;

// Keys from the top-level item down to the item whose route best matches the
// pathname (longest route that equals it or is a parent path of it).
const findMenuKeyPath = (items: IBMenuItem[], pathname: string): string[] => {
  let bestPath: string[] = [];
  let bestLength = 0;

  const walk = (nodes: IBMenuItem[], ancestors: string[]) => {
    nodes.forEach((node) => {
      const keyPath = [...ancestors, node.key];
      const route = node.route;
      if (
        isNotNull(route) &&
        route.length > bestLength &&
        (pathname === route || pathname.startsWith(`${route}/`))
      ) {
        bestPath = keyPath;

        bestLength = route.length;
      }
      if (isNotNull(node.children)) {
        walk(node.children, keyPath);
      }
    });
  };

  walk(items, []);
  return bestPath;
};

const LeftNav = () => {
  const { token } = antTheme.useToken();
  const { mainMenu } = useContext(MainMenuContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const activeKeyPath = useMemo(
    () => findMenuKeyPath(mainMenu, pathname),
    [mainMenu, pathname],
  );
  const selectedKeys = activeKeyPath.slice(-1);
  // Joined so the effect only fires when the active item actually changes.
  const activeAncestors = activeKeyPath.slice(0, -1).join('|');

  useEffect(() => {
    if (activeAncestors === '') {
      return;
    }
    const ancestors = activeAncestors.split('|');
    setOpenKeys((current) =>
      ancestors.every((key) => current.includes(key))
        ? current
        : [...current, ...ancestors.filter((key) => !current.includes(key))],
    );
  }, [activeAncestors]);

  const styles = {
    sider: {
      background: token.colorBgContainer,
      '--ib-nav-guide': token.colorBorder,
      '--ib-nav-active': token.colorPrimary,
      //borderRight: `2px solid ${token.colorBorder}`,
    } as React.CSSProperties,
    fontAwesomeIcon: {
      paddingRight: '10px',
    },
  };

  const handleCollapse = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  const mapMenuItems = (
    items: IBMenuItem[],
    depth: number,
  ): MenuProps['items'] => {
    return items.map((item: IBMenuItem) => {
      return {
        key: item.key,
        title: item.title,
        label: createMenuLabel(item, item.label),
        icon: depth === 0 ? item.icon : null,
        children:
          isNotNull(item.children) && item.children.length > 0
            ? mapMenuItems(item.children, depth + 1)
            : null,
      };
    });
  };

  return (
    <Sider
      collapsible
      width={250}
      style={styles.sider}
      collapsed={collapsed}
      onCollapse={handleCollapse}
    >
      <div
        styleName="logo"
        onClick={() => navigate('/dashboard')}
        style={
          !collapsed &&
          staticBranding === BrandingPreference.INTELLIGENT_BUILDINGS
            ? { margin: '0 0 4rem 0' }
            : {}
        }
      >
        {collapsed && <LeftNavLogoSmall />}
        {!collapsed && <LeftNavLogoLarge />}
      </div>
      <Menu
        className="ib-left-nav"
        theme="light"
        mode="inline"
        inlineIndent={20}
        selectedKeys={selectedKeys}
        // Collapsed mode shows submenus as popups, so leave them uncontrolled there.
        {...(collapsed ? {} : { openKeys, onOpenChange: setOpenKeys })}
        items={mapMenuItems(mainMenu, 0)}
      />
      <TokenExpirationMonitor />
    </Sider>
  );
};

export default LeftNav;
